import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { 
  MessageSquare, User, Search, ChevronDown, ChevronUp,
  Clock, Users as UsersIcon, Bot, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const ChatThreadCard = ({ thread, onSelect }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-3" data-testid="chat-thread-card">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground">
                {thread.participant_details?.map(p => p.full_name || p.email).join(' & ') || 'Unknown Participants'}
              </span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                {thread.message_count} messages
              </span>
            </div>
            <span className="text-sm text-muted-foreground">
              {new Date(thread.created_at).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </div>

      {expanded && thread.last_message && (
        <div className="px-4 pb-4 border-t border-border pt-4">
          <p className="text-sm text-muted-foreground mb-2">Last message:</p>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm text-foreground">{thread.last_message.message}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(thread.last_message.created_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(thread); }}
            className="mt-3 px-4 py-2 rounded-lg bg-primary/20 text-primary text-sm hover:bg-primary/30"
          >
            View Full Conversation
          </button>
        </div>
      )}
    </div>
  );
};

const UserChatDetail = ({ userId, onBack }) => {
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('peer'); // 'peer' or 'ai'
  const [selectedThread, setSelectedThread] = useState(null);

  useEffect(() => {
    const loadUserChats = async () => {
      try {
        const response = await adminAPI.getUserChats(userId);
        setChatData(response.data);
      } catch (error) {
        toast.error('Failed to load user chats');
      } finally {
        setLoading(false);
      }
    };
    loadUserChats();
  }, [userId]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading chat history...</p>
      </div>
    );
  }

  if (!chatData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load chat data</p>
      </div>
    );
  }

  return (
    <div>
      {/* User Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-muted rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <User className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">{chatData.user?.full_name || 'Unknown'}</h2>
          <p className="text-sm text-muted-foreground">{chatData.user?.email}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold text-foreground">{chatData.total_peer_threads}</p>
          <p className="text-sm text-muted-foreground">Peer Conversations</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-2xl font-bold text-foreground">{chatData.total_ai_messages}</p>
          <p className="text-sm text-muted-foreground">AI Messages</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setActiveTab('peer'); setSelectedThread(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            activeTab === 'peer' ? 'bg-primary text-white' : 'bg-muted text-foreground'
          }`}
        >
          <UsersIcon className="w-4 h-4" />
          Peer Chats
        </button>
        <button
          onClick={() => { setActiveTab('ai'); setSelectedThread(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            activeTab === 'ai' ? 'bg-primary text-white' : 'bg-muted text-foreground'
          }`}
        >
          <Bot className="w-4 h-4" />
          AI Companion
        </button>
      </div>

      {/* Selected Thread Messages */}
      {selectedThread && (
        <div className="bg-card border border-border rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Conversation Messages</h3>
            <button
              onClick={() => setSelectedThread(null)}
              className="text-sm text-primary hover:underline"
            >
              Close
            </button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {selectedThread.messages?.map((msg, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg ${
                  msg.sender_id === chatData.user?.user_id 
                    ? 'bg-primary/20 ml-8' 
                    : 'bg-muted mr-8'
                }`}
              >
                <p className="text-sm text-foreground">{msg.message}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(msg.created_at).toLocaleString()}
                  {msg.flagged && <span className="ml-2 text-destructive">(Flagged)</span>}
                </p>
              </div>
            ))}
            {(!selectedThread.messages || selectedThread.messages.length === 0) && (
              <p className="text-center text-muted-foreground py-4">No messages in this thread</p>
            )}
          </div>
        </div>
      )}

      {/* Peer Chats */}
      {activeTab === 'peer' && !selectedThread && (
        <div>
          {chatData.peer_chats?.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <UsersIcon className="w-12 h-12 text-muted mx-auto mb-4" />
              <p className="text-muted-foreground">No peer conversations found</p>
            </div>
          ) : (
            chatData.peer_chats?.map((thread) => (
              <div key={thread.id} className="bg-card border border-border rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-foreground">Thread {thread.id.slice(0, 8)}...</span>
                  <span className="text-sm text-muted-foreground">{thread.message_count || thread.messages?.length || 0} messages</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Started: {new Date(thread.created_at).toLocaleString()}
                </p>
                <button
                  onClick={() => setSelectedThread(thread)}
                  className="px-4 py-2 rounded-lg bg-primary/20 text-primary text-sm hover:bg-primary/30"
                >
                  View Messages
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* AI Chats */}
      {activeTab === 'ai' && (
        <div className="space-y-3">
          {chatData.ai_chats?.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Bot className="w-12 h-12 text-muted mx-auto mb-4" />
              <p className="text-muted-foreground">No AI companion messages found</p>
            </div>
          ) : (
            chatData.ai_chats?.map((msg, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    {msg.role === 'user' ? <User className="w-4 h-4 text-primary" /> : <Bot className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-1">
                      {msg.role === 'user' ? 'User' : 'AI Companion'}
                    </p>
                    <p className="text-sm text-foreground">{msg.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(msg.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const UserChats = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get('user') || null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchAllChats = async () => {
      try {
        const response = await adminAPI.getAllChats();
        setThreads(response.data.threads);
      } catch (error) {
        toast.error('Failed to load chat threads');
      } finally {
        setLoading(false);
      }
    };
    
    if (isAdmin && !selectedUserId) {
      fetchAllChats();
    }
  }, [isAdmin, selectedUserId]);

  // Only admin can access
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSelectThread = (thread) => {
    // Get the first participant that's not the admin
    const participant = thread.participant_details?.find(p => p.user_id !== user.user_id);
    if (participant) {
      setSelectedUserId(participant.user_id);
    }
  };

  if (selectedUserId) {
    return (
      <div className="min-h-screen bg-background py-8" data-testid="user-chats-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <UserChatDetail 
            userId={selectedUserId} 
            onBack={() => setSelectedUserId(null)} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8" data-testid="user-chats-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <MessageSquare className="w-10 h-10 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
              User Chats
            </h1>
            <p className="text-muted-foreground">Monitor peer and AI conversations</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-card border border-border text-foreground"
              data-testid="chat-search-input"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-foreground">{threads.length}</p>
            <p className="text-sm text-muted-foreground">Total Conversations</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-foreground">
              {threads.reduce((sum, t) => sum + (t.message_count || 0), 0)}
            </p>
            <p className="text-sm text-muted-foreground">Total Messages</p>
          </div>
        </div>

        {/* Thread List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading conversations...</p>
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-xl">
            <MessageSquare className="w-12 h-12 text-muted mx-auto mb-4" />
            <p className="text-muted-foreground">No conversations found</p>
          </div>
        ) : (
          <div>
            {threads
              .filter(t => {
                if (!searchQuery) return true;
                const participants = t.participant_details?.map(p => `${p.full_name} ${p.email}`).join(' ') || '';
                return participants.toLowerCase().includes(searchQuery.toLowerCase());
              })
              .map((thread) => (
                <ChatThreadCard
                  key={thread.id}
                  thread={thread}
                  onSelect={handleSelectThread}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
